import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconDownload} from '@tabler/icons-react';
import React from 'react';
import {Link} from 'react-router-dom';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {useAppSelector} from 'src/redux/hooks';
import {useGetDocumentQuery} from 'src/redux/queries/document';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';

const parseAttachmentUrl = (attachmentURL: string): string | undefined => {
  try {
    const parsed = JSON.parse(attachmentURL);
    return parsed?.[0]?.value;
  } catch {
    return undefined;
  }
};

export interface DocumentUnderReviewPanelProps {
  documentId: number;
  documentVersion?: number;
}

/**
 * "Document Under Review" panel for Approval/ToolboxTalk task views — the
 * approver's/acknowledger's primary review surface. Renders the document
 * name, version, summary, and a download link via the existing presigned
 * URL flow.
 */
export const DocumentUnderReviewPanel: React.FC<
  DocumentUnderReviewPanelProps
> = ({documentId, documentVersion}) => {
  const {accessToken} = useAppSelector(state => state.auth);
  const {setAlert} = useAlert();

  // The task pins the exact version it was raised against, which may be neither the
  // active row nor the highest-numbered one — a review-gated update leaves the approved
  // row active while the newer row awaits approval.
  const {data: version, isLoading} = useGetDocumentQuery(
    {id: documentId, version: documentVersion},
    {skip: !documentId}
  );

  const handleDownload = () => {
    if (!version?.attachmentURL) return;
    const url = parseAttachmentUrl(version.attachmentURL);
    if (!url) {
      setAlert('The document attachment could not be opened.', 'error');
      return;
    }
    window.open(getUrlWithEncodingAndAuth(url, accessToken), '_blank');
  };

  if (isLoading) {
    return (
      <Center py="md" data-testid="document-under-review-panel-loading">
        <Loader size="sm" />
      </Center>
    );
  }

  if (!version) {
    return (
      <SectionCard
        title="Document Under Review"
        data-testid="document-under-review-panel">
        <Box p="md">
          <Alert color="red" data-testid="document-under-review-panel-error">
            The document under review could not be loaded. Please refresh the
            page or contact support if the issue persists.
          </Alert>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Document Under Review"
      data-testid="document-under-review-panel">
      <Box p="md">
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start">
            <Stack gap={0}>
              <Text fw={500}>{version.name}</Text>
              <Text size="sm" c="dimmed">
                Version {version.version}
              </Text>
            </Stack>
            {version.attachmentURL && (
              <Button
                size="sm"
                variant="subtle"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownload}>
                Download
              </Button>
            )}
          </Group>
          {version.summaryDescription && (
            <Text size="sm">{version.summaryDescription}</Text>
          )}
          <Group>
            <Anchor
              component={Link}
              to={PathUtility.DocumentManagerViewFile(documentId, 'content')}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              data-testid="document-under-review-panel-open">
              Open in Document Manager
            </Anchor>
          </Group>
        </Stack>
      </Box>
    </SectionCard>
  );
};
